import {
  BRAND_PRESETS,
  BrandColorTokens,
  BrandPresetId,
  isBrandPresetId,
} from './presets';
import { hexToHslComponents } from './color-utils';
import type { PlatformBranding } from '@workspace/types';

export interface ResolvedBrandConfig {
  presetId: BrandPresetId;
  brandName: string;
  logoUrl?: string;
  tokens: BrandColorTokens;
}

type BrandingOverride = Pick<PlatformBranding, 'presetId' | 'brandName' | 'logoUrl'>;

function readEnv(key: string): string | undefined {
  const v = import.meta.env[key as keyof ImportMetaEnv];
  return typeof v === 'string' && v.length > 0 ? v.slice(0, 256) : undefined;
}

function applyHexOverride(
  tokens: BrandColorTokens,
  envKey: string,
  tokenKey: keyof BrandColorTokens,
): void {
  const hex = readEnv(envKey);
  if (!hex) return;
  const hsl = hexToHslComponents(hex);
  if (hsl) tokens[tokenKey] = hsl;
}

export function resolveBrandConfig(override?: BrandingOverride): ResolvedBrandConfig {
  const presetRaw = override?.presetId ?? readEnv('VITE_BRAND_PRESET') ?? 'setup';
  const presetId: BrandPresetId = isBrandPresetId(presetRaw) ? presetRaw : 'setup';
  const base = BRAND_PRESETS[presetId];
  const tokens: BrandColorTokens = { ...base.tokens };

  applyHexOverride(tokens, 'VITE_BRAND_PRIMARY', 'primary');
  applyHexOverride(tokens, 'VITE_BRAND_PRIMARY', 'primary500');
  applyHexOverride(tokens, 'VITE_BRAND_PRIMARY', 'ring');
  applyHexOverride(tokens, 'VITE_BRAND_PRIMARY_FOREGROUND', 'primaryForeground');
  applyHexOverride(tokens, 'VITE_BRAND_SIDEBAR_BG', 'sidebar');
  applyHexOverride(tokens, 'VITE_BRAND_SIDEBAR_FG', 'sidebarForeground');
  applyHexOverride(tokens, 'VITE_BRAND_ACCENT', 'accent');
  applyHexOverride(tokens, 'VITE_BRAND_ACCENT', 'sidebarActive');
  applyHexOverride(tokens, 'VITE_BRAND_SURFACE', 'card');
  applyHexOverride(tokens, 'VITE_BRAND_SURFACE', 'background');
  applyHexOverride(tokens, 'VITE_BRAND_MUTED', 'muted');

  const radius = readEnv('VITE_BRAND_RADIUS');
  if (radius) tokens.radius = radius.slice(0, 16);

  const brandName = override?.brandName ?? readEnv('VITE_BRAND_NAME') ?? base.name;
  const logoUrl = override?.logoUrl ?? readEnv('VITE_BRAND_LOGO_URL');

  return {
    presetId,
    brandName,
    logoUrl,
    tokens,
  };
}
